import {
  Button,
  color,
  InputGroup,
  InputSelect,
  NO_REGEX,
  SWIFT_CODE_REGEX,
} from "@funded-here-interface/common";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStyle } from "../../styles/BankDetailsCss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { bankAccountTypes } from "../../constant/bank";
import BankAccNameDisclaimer from "../../components/BankAccNameDisclaimer/BankAccNameDisclaimer";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { BankFormData } from "./types";
import { INITIAL_ERRORS, TOAST_MESSAGES } from "./constants";
import {
  hydrateBankDataToRedux,
  hydrateBankDataToState,
  validateBankForm,
  validateAccountHolderName,
} from "./utils/bankFormUtils";
import { TabComponentProps } from "../SalesOnboarding/types";
import {
  countries,
  AccountCurrencyTypeEnum,
} from "@funded-here-interface/common/src/constant/country";

const currencyOptions = Object.values(AccountCurrencyTypeEnum);

const BorrowerBank: FC<TabComponentProps> = ({
  isEditable,
  merchantId,
  onDirtyChange,
  onSaveDraft,
  onResetForm,
}) => {
  const { classes } = useStyle();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    bankName: bankNameRedux,
    accountHolderName: accountHolderNameRedux,
    accountNumber: accountNoRedux,
    accountType: accountTypeRedux,
    swiftCode: swiftCodeRedux,
    bankAddress: bankAddressRedux,
    bankCountry: bankCountryRedux,
    currency: currencyRedux,
  } = useSelector((state: RootState) => state.BorBank);

  const comp = useSelector((state: RootState) => state.borBiz.company);
  const userId = merchantId.toString();
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [initPage, setInitPage] = useState(true);

  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialDraftData, setInitialDraftData] = useState<BankFormData | null>(
    null
  );

  const [bankName, setBankName] = useState(bankNameRedux || "");
  const [accountNumber, setAccountNumber] = useState(accountNoRedux || "");
  const [accountType, setAccountType] = useState(accountTypeRedux || "");
  const [swiftCode, setSwiftCode] = useState(swiftCodeRedux || "");
  const [accountHolderName, setAccountHolderName] = useState(
    accountHolderNameRedux || ""
  );
  const [bankAddress, setBankAddress] = useState(bankAddressRedux || "");
  const [bankCountry, setBankCountry] = useState(bankCountryRedux || "");
  const [currency, setCurrency] = useState(currencyRedux || "");

  const [error, setError] = useState(INITIAL_ERRORS);
  const [accountHolderNameError, setAccountHolderNameError] = useState("");

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token,
      };

      const result = await getOrgDraftMutation.mutateAsync(params);

      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (!isEditable) {
        setIsDirty(false);
        onDirtyChange(false);
      }
      if (initPage) {
        try {
          setShouldMarkDirty(false);
          const data = await fetchDraft(parseInt(userId));
          if (data) {
            const bankData: BankFormData = {
              bankName: data.bankName || "",
              accountNumber: data.accountNumber || "",
              accountType: data.accountType || "",
              swiftCode: data.swiftCode || "",
              accountHolderName: data.accountHolderName || "",
              bankAddress: data.bankAddress || "",
              bankCountry: data.bankCountry || "",
              currency: data.currency || "",
            };

            setInitialDraftData(bankData);
            hydrateBankDataToState(bankData, {
              setBankName,
              setAccountNumber,
              setAccountType,
              setSwiftCode,
              setAccountHolderName,
              setBankAddress,
              setBankCountry,
              setCurrency,
            });
            hydrateBankDataToRedux(dispatch, bankData);

            setIsDirty(false);
            onDirtyChange(false);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
          setShouldMarkDirty(true);
        }
      }
    };
    initializeDraft();
  }, [initPage, userId, dispatch]);

  const handleInputChange = (
    setter: (value: string) => void,
    value: string,
    errorField: keyof typeof error
  ) => {
    setter(value);
    setError((prev) => ({ ...prev, [errorField]: "" }));
    if (shouldMarkDirty) {
      setIsDirty(true);
      onDirtyChange(true);
    }
  };

  const handleNumericInputChange =
    (setter: (value: string) => void, errorField: keyof typeof error) =>
    (value: string) => {
      const numericValue = value.replace(NO_REGEX, "");
      handleInputChange(setter, numericValue, errorField);
    };

  const handleSwiftCodeChange = (value: string) => {
    const cleanedValue = value.replace(SWIFT_CODE_REGEX, "");
    handleInputChange(setSwiftCode, cleanedValue, "swiftCode");
  };

  const saveDraft = (showToast: boolean = true) => {
    const isAccountHolderNameValid = validateAccountHolderName(
      accountHolderName,
      comp ?? undefined
    );

    // Show error message below input and toast if account holder name is invalid
    if (!isAccountHolderNameValid) {
      setAccountHolderNameError(TOAST_MESSAGES.ACCOUNT_HOLDER_MISMATCH);
      toast.error(TOAST_MESSAGES.ACCOUNT_HOLDER_MISMATCH, { autoClose: 2000 });
    } else {
      setAccountHolderNameError("");
    }

    // Prepare bank data - only include accountHolderName if valid
    const bankData: BankFormData = {
      bankName,
      accountNumber,
      accountHolderName: isAccountHolderNameValid ? accountHolderName : "",
      accountType,
      swiftCode,
      bankAddress,
      bankCountry,
      currency,
    };

    // Check if there are other fields to save (besides accountHolderName)
    const hasOtherFieldsToSave =
      bankName ||
      accountNumber ||
      accountType ||
      swiftCode ||
      bankAddress ||
      bankCountry ||
      currency;

    // If only accountHolderName is filled and it's invalid, don't save
    if (!isAccountHolderNameValid && !hasOtherFieldsToSave) {
      return;
    }

    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token: token,
        ...bankData,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          onDirtyChange(false);
          setInitialDraftData(bankData);
          hydrateBankDataToRedux(dispatch, bankData);
          if (showToast && hasOtherFieldsToSave) {
            toast.success(TOAST_MESSAGES.DRAFT_SAVED, { autoClose: 2000 });
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const resetForm = () => {
    if (initialDraftData) {
      hydrateBankDataToState(initialDraftData, {
        setBankName,
        setAccountNumber,
        setAccountType,
        setSwiftCode,
        setAccountHolderName,
        setBankAddress,
        setBankCountry,
        setCurrency,
      });
      hydrateBankDataToRedux(dispatch, initialDraftData);

      setError(INITIAL_ERRORS);
      setAccountHolderNameError("");

      setIsDirty(false);
      onDirtyChange(false);
    }
  };

  useEffect(() => {
    onSaveDraft(saveDraft);
    onResetForm(resetForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveDraft, onResetForm]);

  const handleBack = () => {
    saveDraft(false);
    navigate("/bor-rep");
  };

  const handleNext = () => {
    const bankData: BankFormData = {
      bankName,
      accountNumber,
      accountHolderName,
      accountType,
      swiftCode,
      bankAddress,
      bankCountry,
      currency,
    };

    const { valid, errors } = validateBankForm(bankData, comp ?? undefined);

    if (valid) {
      saveDraft(false);
      hydrateBankDataToRedux(dispatch, bankData);
      setIsDirty(false);
      onDirtyChange(false);
      navigate("/bor-doc");
    } else {
      setError(errors);
    }
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <BankAccNameDisclaimer />
      <InputGroup
        isErrored={error.accountHolderName || accountHolderNameError}
        errorMessage={!!(error.accountHolderName || accountHolderNameError)}
        onChange={(value: string) => {
          handleInputChange(setAccountHolderName, value, "accountHolderName");
          if (accountHolderNameError) {
            setAccountHolderNameError("");
          }
        }}
        labelText="Account Holder Name"
        value={accountHolderName}
        id="account_holder_name"
        readOnly={!isEditable}
      />
      <InputGroup
        isErrored={error.bankName}
        onChange={(value: string) =>
          handleInputChange(setBankName, value, "bankName")
        }
        labelText="Bank Name"
        id="bank_name"
        value={bankName}
        readOnly={!isEditable}
      />
      <InputGroup
        isErrored={error.accountNumber}
        onChange={handleNumericInputChange(setAccountNumber, "accountNumber")}
        labelText="Bank Account Number"
        id="account_number"
        value={accountNumber}
        readOnly={!isEditable}
      />
      <InputSelect
        isErrored={error.accountType}
        onChange={(value: string) =>
          handleInputChange(setAccountType, value, "accountType")
        }
        labelText="Bank Account Type"
        id="accountType"
        value={accountType}
        data={bankAccountTypes}
        disabled={!isEditable}
      />
      <InputGroup
        isErrored={error.swiftCode}
        onChange={handleSwiftCodeChange}
        labelText="Bank SWIFT Code"
        value={swiftCode}
        id="swift_code"
        readOnly={!isEditable}
      />
      <InputGroup
        isErrored={error.bankAddress}
        onChange={(value: string) =>
          handleInputChange(setBankAddress, value, "bankAddress")
        }
        labelText="Bank Address"
        value={bankAddress}
        id="bank_address"
        readOnly={!isEditable}
      />
      <InputSelect
        isErrored={error.bankCountry}
        onChange={(value: string) =>
          handleInputChange(setBankCountry, value, "bankCountry")
        }
        labelText="Bank Country"
        id="bank_country"
        value={bankCountry}
        data={countries}
        disabled={!isEditable}
      />
      <InputSelect
        isErrored={error.currency}
        onChange={(value: string) =>
          handleInputChange(setCurrency, value, "currency")
        }
        labelText="Currency"
        id="currency"
        value={currency}
        data={currencyOptions}
        disabled={!isEditable}
      />
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft(true)}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
          disabled={!isEditable}
        />
      </div>
    </div>
  );
};

export default BorrowerBank;
